import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useTranslation } from '../../hooks/useTranslation';
import { profileAPI } from '../../services/api';
import { Upload, X, Building2, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { useToast } from '../ui/use-toast';

const CompanyInfoTab = ({ profile, onUpdate }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    company_name: profile?.company?.company_name || '',
    company_status: profile?.company?.company_status || 'freelancer',
    address: profile?.company?.address || '',
    city: profile?.company?.city || '',
    country: profile?.company?.country || 'France',
    postal_code: profile?.company?.postal_code || '',
    // French compliance fields (mandatory for e-invoicing 2026)
    siren: profile?.company?.siren || '',
    siret: profile?.company?.siret || '',
    vat_number: profile?.company?.vat_number || '',
    naf_code: profile?.company?.naf_code || '',
    rcs: profile?.company?.rcs || '',
    capital: profile?.company?.capital || '',
    legal_form: profile?.company?.legal_form || '',
    // Banking info
    bank_name: profile?.company?.bank_name || '',
    bank_iban: profile?.company?.bank_iban || '',
    bank_bic: profile?.company?.bank_bic || '',
    // Preferences
    default_currency: profile?.company?.default_currency || 'EUR',
    default_payment_terms: profile?.company?.default_payment_terms || 'Net 30',
    default_vat_rate: profile?.company?.default_vat_rate || 20,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(profile?.company?.logo_url || null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Validate SIREN format (9 digits)
  const validateSiren = (siren) => {
    const clean = siren.replace(/\s/g, '');
    return /^\d{9}$/.test(clean);
  };

  // Validate SIRET format (14 digits)
  const validateSiret = (siret) => {
    const clean = siret.replace(/\s/g, '');
    return /^\d{14}$/.test(clean);
  };

  // Calculate compliance score
  const getComplianceStatus = () => {
    const checks = [
      { field: 'siren', valid: validateSiren(formData.siren), required: true },
      { field: 'vat_number', valid: formData.vat_number.length > 0, required: false },
      { field: 'company_name', valid: formData.company_name.length > 0, required: true },
      { field: 'address', valid: formData.address.length > 0, required: true },
    ];
    
    const requiredValid = checks.filter(c => c.required && c.valid).length;
    const requiredTotal = checks.filter(c => c.required).length;
    const allValid = checks.filter(c => c.valid).length;
    
    return {
      isCompliant: requiredValid === requiredTotal,
      score: Math.round((allValid / checks.length) * 100),
      missing: checks.filter(c => c.required && !c.valid).map(c => c.field)
    };
  };

  const compliance = getComplianceStatus();

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    setUploading(true);
    try {
      await profileAPI.uploadLogo(logoFile);
      toast({
        title: 'Logo téléchargé',
        description: 'Votre logo a été mis à jour avec succès',
      });
      onUpdate();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Échec du téléchargement du logo',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await profileAPI.updateCompany(formData);
      toast({
        title: 'Informations mises à jour',
        description: 'Les informations de votre entreprise ont été sauvegardées',
      });
      onUpdate();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Échec de la mise à jour',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Informations entreprise</h3>
          <p className="text-sm text-slate-500 mt-1">
            Données obligatoires pour la facturation électronique 2026
          </p>
        </div>
        <Badge 
          variant="outline" 
          className={compliance.isCompliant 
            ? 'border-emerald-200 text-emerald-700 bg-emerald-50' 
            : 'border-amber-200 text-amber-700 bg-amber-50'
          }
        >
          {compliance.isCompliant ? (
            <CheckCircle className="h-3.5 w-3.5 mr-1" />
          ) : (
            <AlertTriangle className="h-3.5 w-3.5 mr-1" />
          )}
          Conformité: {compliance.score}%
        </Badge>
      </div>

      {/* Compliance Alert */}
      {!compliance.isCompliant && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">
                Conformité e-facturation 2026
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Les champs suivants sont requis pour émettre des factures conformes Factur-X:
              </p>
              <ul className="text-sm text-amber-600 mt-2 space-y-1">
                {compliance.missing.includes('siren') && <li>• Numéro SIREN (9 chiffres)</li>}
                {compliance.missing.includes('company_name') && <li>• Raison sociale</li>}
                {compliance.missing.includes('address') && <li>• Adresse complète</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Logo Upload */}
      <div className="flex items-center space-x-6 p-4 bg-slate-50 rounded-lg">
        <div className="relative">
          {logoPreview ? (
            <img
              src={logoPreview}
              alt="Logo"
              className="h-24 w-24 rounded-lg object-contain bg-white border border-slate-200"
            />
          ) : (
            <div className="h-24 w-24 rounded-lg bg-white border-2 border-dashed border-slate-300 flex items-center justify-center">
              <Building2 className="h-10 w-10 text-slate-400" />
            </div>
          )}
          {logoPreview && (
            <button
              type="button"
              onClick={() => {
                setLogoPreview(null);
                setLogoFile(null);
              }}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-700">Logo entreprise</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Apparaîtra sur vos factures et devis (recommandé: 200x80px)
          </p>
          <Label htmlFor="logo" className="cursor-pointer mt-2 inline-block">
            <div className="flex items-center space-x-2 text-sm text-emerald-600 hover:text-emerald-700">
              <Upload className="h-4 w-4" />
              <span>Choisir une image</span>
            </div>
            <Input
              id="logo"
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="hidden"
            />
          </Label>
          {logoFile && (
            <Button
              type="button"
              onClick={handleLogoUpload}
              disabled={uploading}
              size="sm"
              className="mt-2 bg-emerald-600 hover:bg-emerald-700"
            >
              {uploading ? 'Envoi...' : 'Télécharger'}
            </Button>
          )}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Legal Info Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-slate-800 flex items-center space-x-2">
            <Shield className="h-4 w-4 text-emerald-600" />
            <span>Informations légales</span>
            <Badge variant="outline" className="text-xs">Obligatoire 2026</Badge>
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company_name" className="text-slate-700">
                Raison sociale *
              </Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                className="mt-1"
                placeholder="Ma Société SARL"
                data-testid="company-name-input"
              />
            </div>
            <div>
              <Label htmlFor="legal_form" className="text-slate-700">Forme juridique</Label>
              <Select
                value={formData.legal_form}
                onValueChange={(value) => setFormData({ ...formData, legal_form: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EI">Entreprise Individuelle (EI)</SelectItem>
                  <SelectItem value="EIRL">EIRL</SelectItem>
                  <SelectItem value="AE">Auto-entrepreneur</SelectItem>
                  <SelectItem value="EURL">EURL</SelectItem>
                  <SelectItem value="SARL">SARL</SelectItem>
                  <SelectItem value="SAS">SAS</SelectItem>
                  <SelectItem value="SASU">SASU</SelectItem>
                  <SelectItem value="SA">SA</SelectItem>
                  <SelectItem value="SCI">SCI</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="siren" className="text-slate-700 flex items-center space-x-1">
                <span>SIREN *</span>
                {formData.siren && (
                  validateSiren(formData.siren) ? (
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                  )
                )}
              </Label>
              <Input
                id="siren"
                name="siren"
                value={formData.siren}
                onChange={(e) => setFormData({ ...formData, siren: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                className={`mt-1 ${
                  formData.siren && !validateSiren(formData.siren) 
                    ? 'border-red-300 focus:ring-red-500' 
                    : formData.siren && validateSiren(formData.siren) 
                      ? 'border-emerald-300' 
                      : ''
                }`}
                placeholder="123456789"
                maxLength={9}
                data-testid="siren-input"
              />
              <p className="text-xs text-slate-500 mt-1">9 chiffres</p>
            </div>
            <div>
              <Label htmlFor="siret" className="text-slate-700">SIRET</Label>
              <Input
                id="siret"
                name="siret"
                value={formData.siret}
                onChange={(e) => setFormData({ ...formData, siret: e.target.value.replace(/\D/g, '').slice(0, 14) })}
                className="mt-1"
                placeholder="12345678900001"
                maxLength={14}
              />
              <p className="text-xs text-slate-500 mt-1">14 chiffres (SIREN + NIC)</p>
            </div>
            <div>
              <Label htmlFor="vat_number" className="text-slate-700">N° TVA Intracom.</Label>
              <Input
                id="vat_number"
                name="vat_number"
                value={formData.vat_number}
                onChange={(e) => setFormData({ ...formData, vat_number: e.target.value.toUpperCase() })}
                className="mt-1"
                placeholder="FR12345678901"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="naf_code" className="text-slate-700">Code NAF/APE</Label>
              <Input
                id="naf_code"
                name="naf_code"
                value={formData.naf_code}
                onChange={handleChange}
                className="mt-1"
                placeholder="6201Z"
              />
            </div>
            <div>
              <Label htmlFor="rcs" className="text-slate-700">RCS</Label>
              <Input
                id="rcs"
                name="rcs"
                value={formData.rcs}
                onChange={handleChange}
                className="mt-1"
                placeholder="Paris B 123 456 789"
              />
            </div>
            <div>
              <Label htmlFor="capital" className="text-slate-700">Capital social (€)</Label>
              <Input
                id="capital"
                name="capital"
                type="number"
                value={formData.capital}
                onChange={handleChange}
                className="mt-1"
                placeholder="10000"
              />
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-slate-800">Adresse</h4>
          
          <div>
            <Label htmlFor="address" className="text-slate-700">Adresse *</Label>
            <Input
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="mt-1"
              placeholder="123 Rue Exemple"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="postal_code" className="text-slate-700">Code postal</Label>
              <Input
                id="postal_code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                className="mt-1"
                placeholder="75001"
              />
            </div>
            <div>
              <Label htmlFor="city" className="text-slate-700">Ville</Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="mt-1"
                placeholder="Paris"
              />
            </div>
            <div>
              <Label htmlFor="country" className="text-slate-700">Pays</Label>
              <Input
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="mt-1"
                placeholder="France"
              />
            </div>
          </div>
        </div>

        {/* Banking Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-slate-800">Coordonnées bancaires</h4>
          <p className="text-sm text-slate-500">Pour l'affichage sur vos factures</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="bank_name" className="text-slate-700">Banque</Label>
              <Input
                id="bank_name"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
                className="mt-1"
                placeholder="BNP Paribas"
              />
            </div>
            <div>
              <Label htmlFor="bank_iban" className="text-slate-700">IBAN</Label>
              <Input
                id="bank_iban"
                name="bank_iban"
                value={formData.bank_iban}
                onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value.toUpperCase() })}
                className="mt-1"
                placeholder="FR76 1234 5678 9012 3456 7890 123"
              />
            </div>
            <div>
              <Label htmlFor="bank_bic" className="text-slate-700">BIC/SWIFT</Label>
              <Input
                id="bank_bic"
                name="bank_bic"
                value={formData.bank_bic}
                onChange={(e) => setFormData({ ...formData, bank_bic: e.target.value.toUpperCase() })}
                className="mt-1"
                placeholder="BNPAFRPP"
              />
            </div>
          </div>
        </div>

        {/* Preferences Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-slate-800">Préférences facturation</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="default_currency" className="text-slate-700">Devise par défaut</Label>
              <Select
                value={formData.default_currency}
                onValueChange={(value) => setFormData({ ...formData, default_currency: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="default_payment_terms" className="text-slate-700">Conditions de paiement</Label>
              <Select
                value={formData.default_payment_terms}
                onValueChange={(value) => setFormData({ ...formData, default_payment_terms: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Paiement immédiat</SelectItem>
                  <SelectItem value="Net 15">Net 15 jours</SelectItem>
                  <SelectItem value="Net 30">Net 30 jours</SelectItem>
                  <SelectItem value="Net 45">Net 45 jours</SelectItem>
                  <SelectItem value="Net 60">Net 60 jours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="default_vat_rate" className="text-slate-700">Taux TVA par défaut</Label>
              <Select
                value={formData.default_vat_rate.toString()}
                onValueChange={(value) => setFormData({ ...formData, default_vat_rate: parseFloat(value) })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% - Exonéré</SelectItem>
                  <SelectItem value="2.1">2.1% - Super réduit</SelectItem>
                  <SelectItem value="5.5">5.5% - Réduit</SelectItem>
                  <SelectItem value="10">10% - Intermédiaire</SelectItem>
                  <SelectItem value="20">20% - Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-slate-200">
          <Button 
            type="submit" 
            disabled={saving} 
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-company-btn"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CompanyInfoTab;
